<template>
  <section>
    <header class="flex flex-row items-center gap-3 mb-2">
      <h3 class="font-bold text-xl capitalize">
        {{ $t("channel.signup.profile.title") }}
      </h3>
    </header>

    <form class="space-y-5 py-4" @submit.prevent="submit">
      <div>
        <p class="font-bold text-lg capitalize">
          {{ $t("channel.signup.profile.image") }}
        </p>
        <AddImage v-model="avatar" class="mt-5" size="md" />
      </div>

      <form.Field name="username">
        <template #default="{ field }">
          <div class="space-y-2">
            <Label
              for="username"
              class="text-basics-primary text-xs flex justify-between"
            >
              <span>{{ $t("channel.signup.profile.displayName.label") }}</span>
              <span class="opacity-60">{{
                $t("channel.signup.profile.displayName.required")
              }}</span>
            </Label>

            <input
              :value="field.state.value"
              name="username"
              aria-describedby="username-errors"
              type="text"
              className="grow px-4 py-2 border-brand-tertiary-60 border rounded-lg bg-brand-bgdivider-10 text-basics-primary text-base w-full"
              @input="
                (e) =>
                  field.handleChange(
                    (e.currentTarget as HTMLInputElement).value,
                  )
              "
            />

            <p
              v-if="field.state.meta.errors[0]?.message || errors?.username"
              id="username-errors"
              role="alert"
              class="text-red-500"
            >
              {{ field.state.meta.errors[0]?.message ?? errors?.username }}
            </p>
          </div>
        </template>
      </form.Field>

      <form.Field name="email">
        <template #default="{ field }">
          <div class="space-y-2">
            <Label
              for="email"
              class="text-basics-primary text-xs flex justify-between"
            >
              <span>{{ $t("channel.signup.profile.email.label") }}</span>
              <span class="opacity-60">{{
                $t("channel.signup.profile.email.required")
              }}</span>
            </Label>

            <div class="relative grow">
              <input
                :value="field.state.value"
                name="email"
                aria-describedby="email-errors"
                type="email"
                :readonly="!!validatedEmail"
                className="px-4 py-2 border-brand-tertiary-60 border rounded-lg bg-brand-bgdivider-10 text-basics-primary text-base w-full"
                @input="
                  (e) =>
                    field.handleChange(
                      (e.currentTarget as HTMLInputElement).value,
                    )
                "
              />

              <Icon
                v-if="validatedEmail"
                icon="mdi:lock"
                class="text-basics-primary size-5 absolute right-4 top-1/2 -translate-y-1/2 z-10"
              />
            </div>

            <p
              v-if="field.state.meta.errors[0]?.message || errors?.email"
              id="email-errors"
              role="alert"
              class="text-red-500"
            >
              {{ field.state.meta.errors[0]?.message ?? errors?.email }}
            </p>
          </div>
        </template>
      </form.Field>

      <form.Field name="phone_number.countryCode">
        <template #default="{ field: countryField }">
          <form.Field name="phone_number.number">
            <template #default="{ field: phoneField }">
              <div class="space-y-2">
                <div class="relative grow">
                  <PhoneInput
                    :country-code="countryField.state.value as any"
                    :phone-number="phoneField.state.value"
                    variant="sm"
                    :readonly="!!validatedPhoneNumber"
                    :error="null"
                    @update:country-code="
                      (val) => {
                        countryField.handleChange(val || '');
                        clearFieldErrors('phone_number.number');
                        emit('revalidate', 'phone_number');
                      }
                    "
                    @update:phone-number="
                      (val) => {
                        phoneField.handleChange(val || '');
                        clearFieldErrors('phone_number.number');
                        emit('revalidate', 'phone_number');
                      }
                    "
                  >
                    <template #decoration>
                      <Icon
                        v-if="validatedPhoneNumber"
                        icon="mdi:lock"
                        class="text-basics-primary touch-none pointer-events-none size-5 absolute right-4 top-1/2 -translate-y-1/2 z-10"
                      />
                    </template>
                  </PhoneInput>
                </div>

                <p
                  v-if="
                    phoneField.state.meta.errors[0]?.message ||
                    errors?.phone_number
                  "
                  role="alert"
                  class="text-red-500"
                >
                  {{
                    phoneField.state.meta.errors[0]?.message ??
                    errors?.phone_number
                  }}
                </p>
              </div>
            </template>
          </form.Field>
        </template>
      </form.Field>

      <form.Field name="country_iso">
        <template #default="{ field }">
          <div class="space-y-2">
            <p class="text-basics-primary text-xs flex justify-between">
              <span>{{ $t("channel.signup.profile.country.label") }}</span>
              <span class="opacity-60">{{
                $t("channel.signup.profile.country.required")
              }}</span>
            </p>

            <div
              class="grow px-4 py-2 border-brand-tertiary-60 border rounded-lg bg-brand-bgdivider-10 text-basics-primary text-base w-full flex flex-row justify-between items-center"
            >
              <select
                :value="field.state.value"
                name="country_iso"
                class="font-light ring-0 outline-0 w-full bg-transparent"
                @change="
                  (e) =>
                    field.handleChange(
                      (e.currentTarget as HTMLInputElement).value,
                    )
                "
              >
                <option
                  v-for="country in countriesArray"
                  :key="country.code"
                  :value="country.code"
                >
                  {{ country.name }}
                </option>
              </select>

              <p v-if="errors?.country_iso" role="alert" class="text-red-500">
                {{ errors?.country_iso }}
              </p>
            </div>
          </div>
        </template>
      </form.Field>

      <div class="space-y-2">
        <Label
          for="dob-year"
          class="text-basics-primary text-xs flex justify-between"
        >
          <span>{{ $t("channel.signup.profile.yearOfBirth.label") }}</span>
          <span class="opacity-60">{{
            $t("channel.signup.profile.yearOfBirth.optional")
          }}</span>
        </Label>

        <div
          class="grow px-4 py-2 border-brand-tertiary-60 border rounded-lg bg-brand-bgdivider-10 text-basics-primary text-base"
        >
          <select
            id="dob-year"
            v-model="dobYear"
            name="dob-year"
            class="font-light ring-0 outline-0 w-full bg-transparent"
          >
            <option value="">
              {{ $t("channel.signup.profile.dateOfBirth.year") }}
            </option>
            <option v-for="year in years" :key="year" :value="year">
              {{ year }}
            </option>
          </select>
        </div>

        <p
          v-if="dobError || errors?.date_of_birth"
          role="alert"
          class="text-red-500"
        >
          {{ dobError ?? errors?.date_of_birth }}
        </p>
      </div>

      <div class="flex items-start gap-3 py-2">
        <input
          id="email-consent"
          v-model="emailConsent"
          type="checkbox"
          class="mt-0.5 h-4 w-4 rounded border-brand-tertiary-60 text-brand-primary focus:ring-brand-primary shrink-0"
        />
        <label
          for="email-consent"
          class="text-sm text-basics-primary leading-snug"
        >
          {{ $t("channel.signup.emailConsent.label") }}
        </label>
      </div>

      <div class="space-y-2">
        <ButtonV2
          variant="primary"
          size="md"
          type="submit"
          :disabled="loading"
          @click="submit"
        >
          {{ $t("channel.signup.profile.save") }}
        </ButtonV2>
      </div>
    </form>

    <AgeGateModal
      v-model="showAgeGateModal"
      @confirm="onAgeGateConfirm"
      @cancel="showAgeGateModal = false"
    />
  </section>
</template>

<script setup lang="ts">
import AddImage from "@/components/ui/form/add-image.vue";

import { getBrowserLocale } from "@/utils/locale";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { getAge } from "@/utils/age";

import { useForm } from "@tanstack/vue-form";
import { z } from "zod/v4";
import {
  countries,
  defaultInternationalPhoneNumber,
  type CountryCode,
} from "@/constants/country";
import { Label } from "reka-ui";
import ButtonV2 from "@/components/ui/button/button-v2.vue";
import { useLocation } from "@/composables/use-location";
import phone from "phone";
import { trimTestPhoneNumberPart } from "@/utils/format";
import type { ValidatedUserIdentifier } from "@/generated/graphql";
import { Icon } from "@iconify/vue";
import PhoneInput from "@/components/ui/phone-number/phone-input.vue";
import AgeGateModal from "@/components/channel/auth/common/registration/age-gate-modal.vue";

const emit = defineEmits<{
  submit: [
    user: {
      email: string;
      username: string;
      locale: string;
      avatar: string | null;
      country_iso: string;
      phone_number: string;
      email_consent: boolean | null;
      date_of_birth: string | null;
    },
  ];
  revalidate: [field: string];
}>();

const props = defineProps<{
  loading: boolean;
  validatedIdentifiers: ValidatedUserIdentifier[];
  errors?: {
    email?: string;
    username?: string;
    phone_number?: string;
    country_iso?: string;
    date_of_birth?: string;
  };
}>();

const { t } = useI18n();

const validatedPhoneNumber = computed(() => {
  const full = props.validatedIdentifiers.find(
    (identifier) => identifier.type === "phone_number",
  )?.value;

  if (!full) return null;

  const { real, test } = trimTestPhoneNumberPart(full);
  const { countryCode: countryCode, phoneNumber: numberPart } = phone(real);

  if (!countryCode || !numberPart) return null;

  const countryCodeIso = Object.keys(countries).find((key) =>
    countryCode.includes(countries[key as keyof typeof countries].dial_code),
  );

  if (!countryCodeIso) return null;

  return {
    countryCode: countryCodeIso,
    number: numberPart.replace(countryCode, "") + test,
  };
});

const validatedEmail = computed(() => {
  return props.validatedIdentifiers.find(
    (identifier) => identifier.type === "email",
  )?.value;
});

const avatar = ref<string | null>(null);
const emailConsent = ref(false);

// Date of birth
const dobYear = ref<string>("");
const dobError = ref<string>("");
const showAgeGateModal = ref(false);

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 120 }, (_, i) => currentYear - i);

const countriesArray = Object.keys(countries).map((key) => ({
  code: countries[key as keyof typeof countries].code,
  name: countries[key as keyof typeof countries].name,
  dial_code: countries[key as keyof typeof countries].dial_code,
}));

const schema = z.object({
  email: z
    .string()
    .min(1, t("channel.auth.identityStrategy.validation.emailEmpty"))
    .email({
      error: t("channel.auth.identityStrategy.validation.invalidEmail"),
    }),
  username: z
    .string()
    .min(3, t("channel.auth.identityStrategy.validation.usernameMinLength")),
  country_iso: z
    .string()
    .refine(
      (value): value is CountryCode => value in countries,
      t("channel.auth.identityStrategy.validation.invalidCountryCode"),
    ),
  phone_number: z
    .object({
      countryCode: z.string(),
      number: z
        .string()
        .min(
          1,
          t("channel.auth.identityStrategy.validation.phoneNumberRequired"),
        ),
    })
    .refine(
      (values) =>
        values.number.length === 0 ||
        phone(trimTestPhoneNumberPart(values.number).real, {
          country: values.countryCode,
        }).isValid,
      t("channel.auth.identityStrategy.validation.invalidPhoneNumber"),
    ),
});

const form = useForm({
  defaultValues: {
    email: validatedEmail.value ?? "",
    username: "",
    country_iso: defaultInternationalPhoneNumber.countryCode as string,
    phone_number: validatedPhoneNumber.value ?? defaultInternationalPhoneNumber,
  },
  validators: {
    onChange: schema,
  },
  onSubmit: async ({ value }) => {
    const locale = getBrowserLocale();

    // Validate date of birth
    dobError.value = "";

    if (!dobYear.value) {
      // No year provided — show the age gate modal instead of blocking
      showAgeGateModal.value = true;
      return;
    }

    // Year provided — validate age
    const dob = new Date(parseInt(dobYear.value, 10), 0, 1);
    const age = getAge(dob);
    if (age !== null && age < 13) {
      dobError.value = t("channel.signup.ageGate.error");
      return;
    }

    const { real, test } = trimTestPhoneNumberPart(value.phone_number.number);
    const result = phone(real, {
      country: value.phone_number.countryCode,
    });

    if (!result.isValid) return;

    const fullNumber = result.phoneNumber + test;

    // Format DOB as YYYY-01-01 (year-only, conservative assumption)
    const dobString = `${dobYear.value}-01-01`;

    emit("submit", {
      email: value.email,
      username: value.username,
      phone_number: fullNumber,
      locale,
      avatar: avatar.value,
      country_iso: value.country_iso,
      email_consent: emailConsent.value || null,
      date_of_birth: dobString,
    });
  },
});

const numberPartValue = form.useStore(
  (state) => state.values.phone_number.number,
);

function clearFieldErrors(fieldName: string) {
  form.setFieldMeta(fieldName as any, (prev) => ({
    ...prev,
    errorMap: {
      onChange: undefined,
      onSubmit: undefined,
    },
    errors: [],
  }));
}

const { fetchLocation, watchLocationCountry } = useLocation();

onMounted(() => {
  fetchLocation();
});

watchLocationCountry((country) => {
  form.setFieldValue("country_iso", country);

  // Only update the country code part if the user hasn't started making edits
  // or validated the phone number.
  if (validatedPhoneNumber.value || (numberPartValue.value?.length ?? 0) > 0)
    return;
  form.setFieldValue("phone_number.countryCode", country);
});

function onAgeGateConfirm(year: string) {
  dobYear.value = year;
  form.handleSubmit();
}

function submit() {
  form.handleSubmit();
}
</script>
